// =============================
// ModalPurchaseStripe.js (Professional Receipt Upgrade)
// =============================
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { getValidIdToken } from "../firebase/firebase-handler";
import "./ModalPurchase.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

let openStripeFn = null;
let onCompleteCallback = null;

export function openStripePurchaseModal(json, onComplete) {
  const config = JSON.parse(json);
  onCompleteCallback = onComplete;
  openStripeFn && openStripeFn(config);
}

const formatPrice = (cents) => `$${(cents / 100).toFixed(2)} CAD`;

const ModalContent = ({ userID, item, closeModal }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [receiptData, setReceiptData] = useState(null);

  const price = item?.world_currency_cost;
  const displayName = item?.display_name;
  const desc = item?.desc;
  const isReady = pricing && !loading;

  useEffect(() => {
    if (!item) return;

    const fetchPricing = async () => {
      const token = await getValidIdToken();
      const res = await fetch(
        "https://api.tipsynites.app/payments/stripe/get-price",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ itemId: item.id }),
        }
      );

      const data = await res.json();
      setPricing(data);
    };

    fetchPricing();
  }, [item]);

  const handlePrintReceipt = () => {
    window.print();
  };

  const startStripeCheckout = async () => {
    if (loading) return;

    setError(null);
    setLoading(true);

    try {
      const token = await getValidIdToken();

      const res = await fetch(
        "https://api.tipsynites.app/payments/stripe/create-intent",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: userID, itemId: item.id }),
        }
      );

      const { clientSecret, error } = await res.json();
      if (error) throw new Error(error);

      const card = elements.getElement(CardElement);
      if (!card) throw new Error("Card element not found");

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card },
      });

      if (result.error) {
        setError(result.error.message);
        setLoading(false);
      } else {
        // Build receipt data (client-side fallback)
        const now = new Date();

        setReceiptData({
          itemName: displayName,
          description: desc,
          amount: formatPrice(pricing.total),
          tax: formatPrice(pricing.tax),
          subtotal: formatPrice(pricing.subtotal),
          date: now.toLocaleString(),
          userId: userID,
          itemId: item.id,
          paymentId: result.paymentIntent?.id || "N/A",
        });

        setSuccess(true);
        setLoading(false);
        onCompleteCallback({ success: true });
      }
    } catch (err) {
      setError("Payment failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="purchase-overlay" onClick={() => !loading && closeModal()}>
      <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>

        {/* SUCCESS / RECEIPT */}
        {success ? (
          <div className="receipt print-area">
            <div className="success-header">
              <div className="success-icon">✓</div>
              <h3>Payment Successful</h3>
            </div>

            <div>
              <h2>Receipt</h2>
              <div className="receipt-section">
                <div><strong>Subtotal:</strong> {receiptData.subtotal}</div>
                <div><strong>Tax:</strong> {receiptData.tax}</div>
                <div><strong>Total Paid:</strong> {receiptData.amount}</div>
              </div>

              <div className="receipt-section">
                <div><strong>Total Paid:</strong> {receiptData.amount}</div>
                <div><strong>Date:</strong> {receiptData.date}</div>
              </div>

              <div className="receipt-section">
                <div><strong>Payment ID:</strong> {receiptData.paymentId}</div>
                <div><strong>User ID:</strong> {receiptData.userId}</div>
                <div><strong>Item ID:</strong> {receiptData.itemId}</div>
              </div>
            </div>

            <div className="receipt-footer">
              <p>Thank you for your purchase!</p>
              <p>If you have issues, contact support with this receipt.</p>
            </div>

            <button className="purchase-button stripe" onClick={handlePrintReceipt}>
              Print Receipt
            </button>

            <button className="purchase-button secondary" onClick={closeModal}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h3>Confirm Purchase</h3>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <div className="item-summary">
              <div className="item-left">
                {!imgError && item?.texture_path ? (
                  <img
                    className="item-icon"
                    src={item.texture_path}
                    alt="item"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="item-icon fallback" />
                )}
              </div>

              <div className="item-info">
                <h4>{displayName}</h4>
                <p>{desc}</p>
              </div>

              <div className="price">{formatPrice(price)}</div>
            </div>

            <div className="divider" />

            <div className="total-row">
              <span>Subtotal</span>
              <span>{pricing ? formatPrice(pricing.subtotal) : "..."}</span>
            </div>

            <div className="total-row">
              <span>Tax ({pricing ? (pricing.taxRate * 100).toFixed(0) : 0}%)</span>
              <span>{pricing ? formatPrice(pricing.tax) : "..."}</span>
            </div>

            <div className="total-row total-final">
              <span>Total</span>
              <span>{pricing ? formatPrice(pricing.total) : "..."}</span>
            </div>

            <p className="subtitle">Secure payment powered by Stripe</p>

            <div className="card-element-container">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#fff",              // ← make text white again
                      "::placeholder": {
                        color: "#888"
                      }
                    },
                    invalid: {
                      color: "#ff6b6b"
                    }
                  }
                }}
              />
            </div>

            {error && <div className="error">{error}</div>}

            <button
              className={`purchase-button stripe ${loading ? "loading" : ""}`}
              disabled={!isReady}
              onClick={startStripeCheckout}
            >
              {loading
                ? <span className="spinner"></span>
                : pricing
                  ? `Pay ${formatPrice(pricing.total)}`
                  : "Loading..."}
            </button>

            <button className="cancel" onClick={closeModal}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const ModalPurchaseStripe = () => {
  const [visible, setVisible] = useState(false);
  const [userID, setUserID] = useState(null);
  const [item, setItem] = useState(null);

  useEffect(() => {
    openStripeFn = (config) => {
      setUserID(config?.id);
      setItem(config?.item);
      setVisible(true);
    };
    return () => (openStripeFn = null);
  }, []);

  if (!visible || !item) return null;

  const closeModal = () => {
    setVisible(false);
    setUserID(null);
    setItem(null);
  };

  return ReactDOM.createPortal(
    <Elements stripe={stripePromise}>
      <ModalContent userID={userID} item={item} closeModal={closeModal} />
    </Elements>,
    document.body
  );
};